import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useReaderRoutes } from '../providers/ReaderRoutesProvider';
import { getAccessToken } from '../auth';
import { hideStory, hiddenStories, unhideStory, explainHide, type HiddenStory, type HideReason } from '../repositories/readerFeedbackRepository';
import { readerEvent } from '../utils/readerTelemetry';
import { rememberReaderAction, RESUMED_HIDE_KEY } from '../utils/readerPendingAction';
import { hideOriginProperties, hideSurfaceOf, hideViewOf, rowsHidingStory } from '../utils/hideScope';
import { localPreferencesAccountId } from '../utils/localReaderPreferences';
import { StoryActions, HiddenStoryRow } from './ReaderStoryFeedback';
import { isModalOpen } from './ModalOverlay';
import type { FeedStory } from '../repositories/readerStory';
import type { StoryElement } from '../types';
import { safeArticleUrl } from '../utils/readerInterests';
import { useStoryKeyboardNav } from '../hooks/useStoryKeyboardNav';
import { StoryPermalinkButton } from './StoryPermalinkButton';
import { GatedMarker } from './GatedMarker';
import { MediaKindMarker } from './MediaKindMarker';
import { DiscussionSection } from './DiscussionSection';
import { displayScore, fmt } from '../utils/scoreBreakdown';
import { rankFactors, type MixPlacement } from '../utils/rankAudit';
import { getStoryAlternates, getStoryContext, recordEvent, type UserEventContext } from '../repositories/storyRepository';
import { formatAge } from '../utils/formatTime';
import { decodeHtmlEntities, safeHref, stripHtml } from '../utils/html';
import { formatStoryTitle } from '../utils/storyTitle';
import { hasTopicContext } from '../utils/storySummaryHelpers';
import { readerTopicSources, topicContextSourceLinks, topicContextSourcesLabel } from '../utils/topicContextSources';
import { servedArticleAdminHref, type ServedRow } from '../utils/articleAdminLink';
import { readerDomainLabel } from '../utils/storyLinks';
import { arrivedStoryIds, storyNames } from '../utils/storyArrivals';
import { movedStoryIds } from '../utils/storyMoves';

type HidePromptEnd = 'another_hide' | 'unhide' | 'left_view' | 'page_closed';

// These regions retain native selection and control behavior; card gutters toggle.
const STORY_TEXT_REGION = '.beta-story-heading, .beta-story-summary, .feed-item-details';
const STORY_CONTROL = 'a, button, input, textarea, select, [contenteditable="true"]';

const DEFAULT_EVENT_CONTEXT: UserEventContext = { surface: 'unknown' };

interface BetaStoryRowProps {
  eventContext: UserEventContext;
  article: FeedStory;
  index: number;
  isExpanded: boolean;
  isFocused: boolean;
  engaged: boolean;
  /** It just arrived above stories already shown (utils/storyArrivals) or a ranking moved it (utils/storyMoves):
   * highlighted until its animation ends. */
  arrived: boolean | undefined;
  onArrivalShown: (id: string) => void;
  onHide: (article: FeedStory) => void;
  isAdmin: boolean;
  /** For You's mix placement of this story, when For You served it. */
  mix?: MixPlacement;
  showInterests: boolean;
  setRowRef: (el: HTMLElement | null) => void;
  onFocus: (id: string) => void;
  onToggleExpanded: (id: string) => void;
}

const BetaStoryRow = memo(function BetaStoryRow({
  eventContext,
  article,
  index: _index,
  isExpanded,
  isFocused,
  engaged,
  arrived,
  onArrivalShown,
  onHide,
  isAdmin,
  mix,
  showInterests,
  setRowRef,
  onFocus,
  onToggleExpanded,
}: BetaStoryRowProps) {
  const [fetchedAlts, setFetchedAlts] = useState<NonNullable<StoryElement['alternatives']> | null>(null);
  const [loadingAlts, setLoadingAlts] = useState(false);
  const [fetchedContext, setFetchedContext] = useState<StoryElement['topic_context'] | null>(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const pointerStartedInText = useRef(false);
  const routes = useReaderRoutes();
  const context = article.topic_context ?? fetchedContext ?? undefined;
  const hasContext = hasTopicContext({ topic_context: context });

  const url = safeArticleUrl(article.linked_url || article.url);
  const domainLabel = readerDomainLabel(article);
  const titleText = formatStoryTitle(article.friendly_title || article.title);
  const summaryText = stripHtml(article.summary);

  const inlineAlts = article.alternatives || [];
  const rawAlts = fetchedAlts !== null
    ? [...inlineAlts, ...fetchedAlts]
    : inlineAlts;
  const sourceStory = { ...article, topic_context: context, alternatives: rawAlts };
  const alts = readerTopicSources(sourceStory);
  const pendingMateCount = fetchedAlts === null ? (article.topic_mate_count || 0) : 0;
  // A one-article story citing itself under every bullet says nothing.
  const singleArticle = alts.length === 0 && pendingMateCount === 0;

  const loadAlternatesIfNeeded = async () => {
    if (fetchedAlts !== null || loadingAlts || !(article.topic_mate_count || 0)) return;
    setLoadingAlts(true);
    try {
      setFetchedAlts(await getStoryAlternates(article.id));
    } catch {
      setFetchedAlts([]);
    } finally {
      setLoadingAlts(false);
    }
  };

  // Lists defer context (`has_topic_context`); it is read once, when the row is first opened.
  const loadContextIfNeeded = async () => {
    if (article.topic_context !== undefined || !article.has_topic_context || fetchedContext !== null || loadingContext) return;
    setLoadingContext(true);
    try {
      setFetchedContext(await getStoryContext(article.id));
    } catch {
      setFetchedContext([]);
    } finally {
      setLoadingContext(false);
    }
  };

  useEffect(() => {
    if (!isExpanded) return;
    void loadAlternatesIfNeeded();
    void loadContextIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  return (
    <details
      className={`beta-story${isFocused ? ' keyboard-focused' : ''}${isFocused && engaged ? ' focus-engaged' : ''}${arrived !== undefined ? ` is-arrived${arrived ? ' is-arrived-again' : ''}` : ''}`}
      open={isExpanded}
      ref={setRowRef}
      tabIndex={-1}
      onAnimationEnd={(event) => { if (arrived !== undefined && event.target === event.currentTarget) onArrivalShown(article.id); }}
      onPointerDown={(event) => {
        pointerStartedInText.current = Boolean((event.target as HTMLElement).closest(STORY_TEXT_REGION));
      }}
      onClick={(event) => {
        const target = event.target as HTMLElement;
        const startedInText = pointerStartedInText.current;
        pointerStartedInText.current = false;
        onFocus(article.id);
        if (target.closest(STORY_CONTROL)) return;
        // Cancel native summary activation, including when selecting its metadata.
        if (target.closest('summary')) event.preventDefault();
        // Keyboard activation of the summary remains a disclosure action.
        const keyboardActivation = event.detail === 0 && target === event.currentTarget.querySelector('summary');
        if (!keyboardActivation) {
          if (startedInText || target.closest(STORY_TEXT_REGION)) return;
          const selection = window.getSelection();
          if (selection && !selection.isCollapsed && selection.containsNode(event.currentTarget, true)) return;
        }
        if (!isExpanded) void recordEvent(article.id, 'show_summary', eventContext);
        onToggleExpanded(article.id);
      }}
      onAuxClick={(event) => {
        if (event.button !== 1) return;
        const anchor = (event.target as HTMLElement).closest('a');
        if (!anchor || anchor.target !== '_blank') return;
        void recordEvent(anchor.dataset.articleId || article.id, 'click_external', {
          ...eventContext, destinationUrl: anchor.href,
        });
      }}
    >
      <summary>
        <span className="beta-story-heading">
          {url ? (
            <a
              href={url}
              className="beta-story-title beta-story-title-marked"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                onFocus(article.id);
                void recordEvent(article.id, 'click_external', {
                  ...eventContext,
                  destinationUrl: url,
                });
              }}
            >
              <span className="beta-story-title-text">{titleText}</span>
              <GatedMarker isGated={article.is_gated} />
              <MediaKindMarker kind={article.media_kind} />
            </a>
          ) : (
            <span className="beta-story-title beta-story-title-marked">
              <span className="beta-story-title-text">{titleText}</span>
              <GatedMarker isGated={article.is_gated} />
              <MediaKindMarker kind={article.media_kind} />
            </span>
          )}
          <span className="beta-story-meta">
            {isAdmin && <AdminRank article={article} mix={mix} href={servedArticleAdminHref(article, eventContext)} />}
            <StoryPermalinkButton story={article} surface={eventContext.surface} />
            {domainLabel && <span className="beta-story-domain">{domainLabel}</span>}
            {isAdmin && (article.published_at || article.created_at) && (
              <span className="beta-story-age">
                {formatAge((article.published_at || article.created_at)!)}
              </span>
            )}
            {article.estimated_reading_minutes ? <span className="beta-story-reading-time">{article.estimated_reading_minutes} min read</span> : null}
            {showInterests && article.for_you_searches && article.for_you_searches.length > 0 && (
              // Readers see at most three chips; an admin sees every feed the
              // story matched, with its per-feed score.
              (isAdmin ? article.for_you_searches : article.for_you_searches.slice(0, 3)).map((search) => (
                <Link key={search.id} to={routes.feed(search.id)} className="beta-story-interest" onClick={event => {
                  event.stopPropagation();
                  readerEvent('story_feed_click', { story_id: article.id, feed_id: search.id, surface: eventContext.surface, surface_id: eventContext.surfaceId });
                }}>
                  {search.name}{isAdmin && search.score != null ? ` (${search.score.toFixed(2)})` : ''}
                </Link>
              ))
            )}
          </span>
        </span>
        <StoryActions article={article} expanded={isExpanded} onHide={() => onHide(article)} onExpand={() => {
          onFocus(article.id);
          if (!isExpanded) void recordEvent(article.id, 'show_summary', eventContext);
          onToggleExpanded(article.id);
        }} />
      </summary>
      <div className="beta-story-summary">
        {summaryText ? (article.summary_is_quote ? `“${summaryText}”` : summaryText) : 'A summary isn’t available for this article.'}{' '}
        {article.summary && article.summary_source?.domain && (
          <span className="beta-story-summary-source">{article.summary_is_quote ? `(from ${article.summary_source.domain})` : `Summary via ${article.summary_source.domain}.`}{' '}</span>
        )}
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => void recordEvent(article.id, 'click_external', {
              ...eventContext,
              destinationUrl: url,
            })}
          >
            Open article →
          </a>
        )}
      </div>
      {(hasContext || loadingContext || alts.length > 0 || pendingMateCount > 0 || Boolean(article.discussion)) && (
        <div className="feed-item-expanded feed-item-details">
          {loadingContext && (
            <div className="feed-item-details-empty">Loading context...</div>
          )}
          {loadingAlts && alts.length === 0 && (
            <div className="feed-item-details-empty">Loading other sources...</div>
          )}
          {hasContext && context && context.map((cat) => (
            <section className="feed-item-expanded-section" key={cat.name}>
              <div className="feed-item-section-header">{cat.name}</div>
              <ul className="feed-item-context-list">
                {cat.facts.map((fact, fi) => {
                  const sourceLinks = topicContextSourceLinks(sourceStory, fact.sources, fact.source_links);
                  const sourcesLabel = topicContextSourcesLabel(sourceLinks, article.id, singleArticle);
                  return (
                    <li key={fi} className="feed-item-context-fact">
                      <span className="feed-item-context-text">{fact.text}</span>
                      {sourcesLabel && (
                        <span className="feed-item-context-sources">
                          <span className="feed-item-context-sources-label">{sourcesLabel}: </span>
                          {sourceLinks.map((source, sourceIndex) => (
                            <span key={`${source.label}-${sourceIndex}`}>
                              {sourceIndex > 0 && ', '}
                              {source.href ? <a
                                href={source.href}
                                data-article-id={source.articleId || article.id}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void recordEvent(source.articleId || article.id, 'click_external', {
                                    ...eventContext,
                                    destinationUrl: safeHref(source.href),
                                  });
                                }}
                              >
                                {source.label}
                              </a> : <span>{source.label}</span>}
                            </span>
                          ))}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
          {article.discussion && (
            <DiscussionSection
              discussion={article.discussion}
              onLinkClick={(discussionUrl) => void recordEvent(article.id, 'click_external', {
                ...eventContext,
                destinationUrl: discussionUrl,
              })}
            />
          )}
          {alts.length > 0 && (
            <section className="feed-item-expanded-section">
              <div className="feed-item-section-header">
                More on this topic
              </div>
              <ul className="feed-item-alternates-list">
                {alts.map((alt) => (
                  <li key={alt.url}>
                    <a
                      href={safeHref(alt.url)}
                      data-article-id={alt.id}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.stopPropagation();
                        void recordEvent(alt.id, 'click_external', {
                          ...eventContext,
                          destinationUrl: alt.url,
                        });
                      }}
                    >
                      {decodeHtmlEntities(alt.title) || '(Untitled)'}
                    </a>
                    <GatedMarker isGated={alt.is_gated} />
                    <MediaKindMarker kind={alt.media_kind} />
                    <span className="feed-item-alternates-meta">
                      {alt.canonical_domain && (
                        <span className="feed-item-alternates-domain">{alt.canonical_domain}</span>
                      )}
                      {alt.via_url && (
                        <span><a
                          href={alt.via_url}
                          data-article-id={alt.id}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(event) => {
                            event.stopPropagation();
                            void recordEvent(alt.id, 'click_external', {
                              ...eventContext, destinationUrl: alt.via_url,
                            });
                          }}
                        >via {alt.via_domain}</a></span>
                      )}
                      {alt.published_at && (
                        <span className="feed-item-alternates-age">{formatAge(alt.published_at)}</span>
                      )}
                      {alt.estimated_reading_minutes != null && (
                        <span className="feed-item-alternates-reading">{alt.estimated_reading_minutes} min</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </details>
  );
});

/**
 * The admin badge: the number this list is ordered by, then what it is made of
 * (see utils/rankAudit). The badge opens the article's admin page.
 */
function AdminRank({ article, mix, href }: { article: ServedRow; mix?: MixPlacement; href: string }) {
  const factors = rankFactors(article);
  // A row no v2 build ranked (a permalink, /fulltextsearch) is ordered by its legacy feed score.
  const score = factors ? (mix ?? factors).score : displayScore(article);
  if (score == null) return null;
  return <>
    <a className="feed-item-base-score" href={href} target="_blank" rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()} title="Open the article's scoring and enrichment page">
      {score.toFixed(2)}
    </a>
    {factors && (
      <span className="beta-story-rank-factors">
        = grade {fmt(factors.grade)} × rel {factors.relevance.toFixed(2)} × decay {factors.decay == null ? '–' : factors.decay.toFixed(2)} ({Math.round(factors.ageHours)}h)
        {mix && mix.placed > 0 && ` × same-feed ${mix.factor.toFixed(2)} (${mix.placed} ${mix.feed} above)`}
      </span>
    )}
  </>;
}

export function BetaStoryList({
  eventContext = DEFAULT_EVENT_CONTEXT,
  articles,
  showInterests = false,
  resetKey = 'beta-stories',
  autoFocusFirst: _autoFocusFirst = true,
  isAdmin = false,
  mix,
  initiallyExpanded = false,
  unservedIds,
  settled = true,
}: {
  eventContext?: UserEventContext;
  articles: FeedStory[];
  showInterests?: boolean;
  resetKey?: string;
  autoFocusFirst?: boolean;
  isAdmin?: boolean;
  /** For You's mix placements by story id (utils/rankAudit.forYouMix), shown in the admin badge. */
  mix?: ReadonlyMap<string, MixPlacement>;
  /** Open every row on mount; the permalink page shows one story and wants it read, not teased. */
  initiallyExpanded?: boolean;
  /** Stories shown but not served by this list (For You's delivered stories, an earlier load's): never reported. */
  unservedIds?: ReadonlySet<string>;
  /** False while the order can still change: no row rests focused until it is true. */
  settled?: boolean;
}) {
  // Every active hide, newest first. A story can have several (hidden in X, then in Y);
  // hideScope decides which of them hide it in this view.
  const [hides, setHides] = useState<HiddenStory[]>([]);
  // The hide whose reason prompt is open. It always hides its story here: a hide applies to its own view.
  const [prompt, setPrompt] = useState<HiddenStory | null>(null);
  // How the open reason prompt ends when no reason is picked; see story_hide_no_reason.
  const promptEnd = useRef<{ answered: boolean; endedBy: HidePromptEnd }>({ answered: false, endedBy: 'left_view' });
  useEffect(() => {
    if (!prompt) return;
    promptEnd.current = { answered: false, endedBy: 'left_view' };
    const end = (endedBy: HidePromptEnd) => {
      if (promptEnd.current.answered) return;
      promptEnd.current.answered = true;
      readerEvent('story_hide_no_reason', { story_id: prompt.story_id, feedback_id: prompt.id, ended_by: endedBy });
    };
    const onPageHide = () => end('page_closed');
    window.addEventListener('pagehide', onPageHide);
    return () => { window.removeEventListener('pagehide', onPageHide); end(promptEnd.current.endedBy); };
  }, [prompt?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- a reason edit keeps the same prompt
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  // Keyed on the account, not the token string: a token refresh must not reset hides.
  const accountId = localPreferencesAccountId();
  const view = useMemo(() => hideViewOf(eventContext), [eventContext]);
  const hiding = useMemo(() => {
    const byStory = new Map<string, HiddenStory[]>();
    hides.forEach(row => byStory.set(row.story_id, [...(byStory.get(row.story_id) || []), row]));
    return new Map(articles.map(article => [article.id, rowsHidingStory(byStory.get(article.id) || [], view,
      (article.for_you_searches || []).map(feed => feed.id))]));
  }, [articles, hides, view]);
  const visible = useMemo(() => articles.filter(a => !hiding.get(a.id)?.length), [articles, hiding]);
  // Rankings apply as they arrive. One that pushes the reader's row out of view sends
  // them to the top row, under a brief "New results found" (counted, to replay it).
  const [newResults, setNewResults] = useState(0);
  const newResultsRef = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => setNewResults(0), [resetKey]);
  useLayoutEffect(() => { if (newResults) newResultsRef.current?.scrollIntoView({ block: 'nearest' }); }, [newResults]);
  // Stories a ranking puts above ones the reader was already shown, and stories it moves up or down the list,
  // pulse briefly where they land, so the shift has a visible cause (utils/storyArrivals, utils/storyMoves;
  // moves are judged against the ranking on screen just before this one). Measured before paint, so a row
  // lands already highlighted.
  const shownNames = useRef<{ key: string; names: Set<string>; stories: readonly FeedStory[] }>(
    { key: resetKey, names: new Set(), stories: [] });
  const [arrived, setArrived] = useState<ReadonlyMap<string, boolean>>(() => new Map());
  useLayoutEffect(() => {
    const reset = shownNames.current.key !== resetKey;
    if (reset) shownNames.current = { key: resetKey, names: new Set(), stories: [] };
    const { names, stories: previous } = shownNames.current;
    const landed = [...arrivedStoryIds(names, articles), ...movedStoryIds(previous, articles)];
    articles.forEach(article => storyNames(article).forEach(name => names.add(name)));
    shownNames.current.stories = articles;
    // A removed row cannot fire animationend. Forget it so a later return does not replay a stale pulse.
    const currentIds = new Set(visible.map(article => article.id));
    setArrived(value => {
      const next = new Map(reset ? [] : [...value].filter(([id]) => currentIds.has(id)));
      new Set(landed.filter(id => currentIds.has(id))).forEach(id => next.set(id, !next.get(id)));
      return next.size === value.size && [...next].every(([id, beat]) => value.get(id) === beat) ? value : next;
    });
  }, [articles, visible, resetKey]);
  const arrivalShown = useCallback((id: string) => setArrived(value => {
    const next = new Map(value);
    next.delete(id);
    return next;
  }), []);
  const { focusedIndex, isFocusActive, expandedIds, focusStory, toggleExpanded, setRowRef } =
    useStoryKeyboardNav(visible, resetKey, eventContext, {
      initiallyExpanded, readerFocus: true, settled, onPushedOutOfView: () => setNewResults(count => count + 1) });
  // One served list per feed/search: results that arrive later join the same list,
  // and each story is reported once, at the position it first appeared.
  const servedId = useMemo(() => crypto.randomUUID(), [resetKey, eventContext.surface, eventContext.surfaceId]);
  const servedStories = useRef<{ id: string; ids: Set<string> } | null>(null);
  useEffect(() => {
    if (servedStories.current?.id !== servedId) servedStories.current = { id: servedId, ids: new Set() };
    const reported = servedStories.current.ids;
    const fresh = articles.map((article, index) => ({ id: article.id, position: index + 1 }))
      .filter(item => !reported.has(item.id) && !unservedIds?.has(item.id));
    fresh.forEach(item => reported.add(item.id));
    for (let offset = 0; offset < fresh.length; offset += 64) {
      const batch = fresh.slice(offset, offset + 64);
      readerEvent('feed_served', {
        ...eventContext.properties, feed_served_id: servedId, position_start: batch[0].position,
        positions: batch.map(item => item.position),
        surface: eventContext.surface, surface_id: eventContext.surfaceId, story_ids: batch.map(item => item.id) });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servedId, articles]);
  useEffect(() => {
    let disposed = false;
    setHides([]);
    if (accountId) hiddenStories().then(rows => {
      if (disposed) return;
      // A hide made while this load was in flight is not in rows; keep it.
      setHides(value => [...value.filter(row => !rows.some(loaded => loaded.id === row.id)), ...rows]);
      const resumed = sessionStorage.getItem(RESUMED_HIDE_KEY);
      if (resumed) { setPrompt(rows.find(row => row.id === resumed) || null); sessionStorage.removeItem(RESUMED_HIDE_KEY); }
    }).catch(() => { if (!disposed) setError('Could not load hidden stories. Reload to try again.'); });
    return () => { disposed = true; };
  }, [accountId]);
  const handleHide = useCallback(async (article: FeedStory) => {
    if (busy) return;
    const properties = { ...eventContext.properties, feed_served_id: servedId,
      position: articles.findIndex(a => a.id === article.id) + 1,
      matched_feed_ids: (article.for_you_searches || []).map(feed => feed.id),
      query: eventContext.properties?.query || null, ...hideOriginProperties(view) };
    const input = { id: crypto.randomUUID(), story_id: article.id, surface: hideSurfaceOf(view), properties };
    if (!getAccessToken()) {
      rememberReaderAction({ kind: 'hide', input });
      readerEvent('login_gate_hit', { trigger_action: 'hide', trigger: 'hide' });
      navigate('/signup', { state: { backgroundLocation: location, from: location } }); return;
    }
    setBusy(true); setError('');
    try {
      const saved = await hideStory(input);
      const index = visible.findIndex(a => a.id === article.id);
      const next = visible[index + 1] || visible[index - 1];
      if (next) focusStory(next.id);
      promptEnd.current.endedBy = 'another_hide';
      setHides(value => [saved, ...value]); setPrompt(saved);
      readerEvent('story_hide', { ...properties, story_id: article.id, feedback_id: saved.id, surface: input.surface });
    } catch { setError('Could not hide this story. Please try again.'); }
    finally { setBusy(false); }
  }, [busy, eventContext, servedId, articles, visible, view, focusStory, navigate, location]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (isModalOpen() || target.closest('input, textarea, select, [contenteditable="true"]') || event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key === 'x' && !event.repeat && visible[focusedIndex]) { event.preventDefault(); void handleHide(visible[focusedIndex]); }
    };
    // The reason prompt stays until a reason is chosen or the story is unhidden.
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleHide, visible, focusedIndex]);
  const handleReason = async (row: HiddenStory, reason: HideReason, label: string, properties: Record<string, unknown> = {}, text = '') => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await explainHide(row.id, reason, text, { ...properties, label });
      setHides(value => value.map(item => item.id === row.id ? { ...item, reason, text, properties: { ...item.properties, ...properties, label } } : item));
      promptEnd.current.answered = true;
      setPrompt(null); readerEvent(`story_hide_${reason}`, { story_id: row.story_id, feedback_id: row.id, label, text, ...properties });
    } catch { setError('Could not save the reason. Please try again.'); }
    finally { setBusy(false); }
  };
  // Unhide restores the story in this view: it retracts every hide that hides it here
  // and leaves hides scoped to other feeds or searches in place.
  const handleUnhide = async (articleId: string) => {
    if (busy) return;
    const ids = (hiding.get(articleId) || []).map(row => row.id);
    setBusy(true); setError('');
    try {
      await unhideStory(articleId, ids);
      setHides(value => value.filter(row => !ids.includes(row.id)));
      promptEnd.current.endedBy = 'unhide';
      setPrompt(null); setRestored(articleId);
      readerEvent('story_unhide', { story_id: articleId, feedback_ids: ids, surface: hideSurfaceOf(view) });
    } catch { setError('Could not unhide this story. Please try again.'); }
    finally { setBusy(false); }
  };
  const [restored, setRestored] = useState<string | null>(null);
  useEffect(() => { if (restored && visible.some(a => a.id === restored)) { focusStory(restored); setRestored(null); } }, [restored, visible, focusStory]);
  return (
    <div className="beta-stories">
      {newResults > 0 && <p key={newResults} ref={newResultsRef} className="reader-new-results" role="status">New results found</p>}
      {error && <p className="beta-error" role="alert">{error}</p>}
      {articles.length > 0 && visible.length === 0 && <p className="beta-message">All stories are hidden. Unhide a story below or try another feed.</p>}
      {articles.map((article, index) => { const rows = hiding.get(article.id) || []; const promptRow = rows.find(row => row.id === prompt?.id); return rows.length ? (
        <HiddenStoryRow key={article.id} article={article} prompt={!!promptRow} busy={busy}
          query={view.kind === 'query' ? view.query : undefined}
          onReason={(reason, label, properties, text) => { if (promptRow) void handleReason(promptRow, reason, label, properties, text); }}
          onUnhide={() => void handleUnhide(article.id)} />
      ) : (
        <BetaStoryRow
          key={article.id}
          article={article}
          eventContext={eventContext}
          index={index}
          isExpanded={expandedIds.has(article.id)}
          isFocused={visible[focusedIndex]?.id === article.id}
          engaged={isFocusActive}
          arrived={arrived.get(article.id)}
          onArrivalShown={arrivalShown}
          onHide={handleHide}
          isAdmin={isAdmin}
          mix={mix?.get(article.id)}
          showInterests={showInterests}
          setRowRef={setRowRef(visible.findIndex(a => a.id === article.id))}
          onFocus={focusStory}
          onToggleExpanded={toggleExpanded}
        />
      ); })}
    </div>
  );
}
